import { DateTime } from '@ksyos/date-time';
import { BatchType } from '@common/enums';
import type { Financing } from '../financing';

export function getSelectedItemsInDateOrder(financingItems: Financing[], selectedFinancingItemIds: string[]) {
    return financingItems
        .filter((item) =>
            selectedFinancingItemIds.some((id) => id === item.id && getIsUsedForReprocessing(financingItems, item))
        )
        .sort((a, b) => DateTime.from(a.created_at).diff(b.created_at));
}

export function getIsUsedForReprocessing(array: Financing[], item: Financing) {
    if (item.isMigratedProcess) {
        return false;
    }
    const hasNonNullId =
        item.declaration.id === null &&
        item.invoice.id === null &&
        item.patientInvoice.id === null &&
        item.singularPatientInvoice.id === null;

    const activityFinancialItems = array.filter((financialItem) => financialItem.id === item.id);

    const isError = item.fundingError || item.notFundedReason;

    const hasErrorInActivityItems = activityFinancialItems.some(
        (financialItem) => financialItem.notFundedReason || financialItem.fundingError
    );

    if (hasNonNullId || activityFinancialItems.length === 1 || isError) {
        return true;
    }
    if (hasErrorInActivityItems) {
        return false;
    }

    const financingType = getBatchTypeFromActivity(item);

    if (!financingType) {
        return false;
    }

    const filteredActivityFinancialItems = activityFinancialItems
        .filter((financialItem) => {
            return (
                (financialItem.declaration.id !== null && financialItem.declaration.counterBookingId === null) ||
                (financialItem.invoice.id !== null && financialItem.invoice.counterBookingId === null) ||
                (financialItem.patientInvoice.id !== null &&
                    financialItem.patientInvoice.counterBookingRunNumber === null) ||
                (financialItem.singularPatientInvoice.id !== null &&
                    financialItem.singularPatientInvoice.counterBookingId === null)
            );
        })
        .sort(sortbyCounterBooking);

    return filteredActivityFinancialItems[0]?.[financingType].id === item[financingType].id;
}

function sortbyCounterBooking(a: Financing, b: Financing) {
    const aCreatedAt = getCreatedAtFromActivity(a);
    const bCreatedAt = getCreatedAtFromActivity(b);
    if (aCreatedAt === null || bCreatedAt === null) {
        return 0;
    }
    if (aCreatedAt !== bCreatedAt) {
        return DateTime.from(aCreatedAt).diff(bCreatedAt);
    }
    if (b.patientInvoice.isCounterBooked !== a.patientInvoice.isCounterBooked) {
        return b.patientInvoice.isCounterBooked ? 1 : -1;
    }

    return 0;
}

function getCreatedAtFromActivity(activity: Financing) {
    return activity.invoice.created_at ?? activity.patientInvoice.created_at ?? activity.declaration.created_at;
}

function getBatchTypeFromActivity(activity: Financing) {
    if (activity.invoice.id) {
        return BatchType.invoice;
    } else if (activity.patientInvoice.id) {
        return BatchType.patientInvoice;
    } else if (activity.declaration.id) {
        return BatchType.declaration;
    } else if (activity.singularPatientInvoice.id) {
        return BatchType.singularPatientInvoice;
    }
}
